import { cloneDeep, isEmpty, isEqual, uniqWith } from "lodash";
import { batch, useDispatch, useSelector } from "react-redux";
import { requestAsync } from "redux-query";
import { getDatapointGridStateId } from "../grid/common";
import { getDatapointsExcelComparisonRequestQueryConfig, getDatapointsExcelRequestQueryConfig, useGetSubEntityName } from "../grid/excelExportOptions";
import { filterHideParamsForInspectData } from "./helper";
import { getInspectDataTabsRequestParams, isPacingTimeseries } from "./helpers/get-inspect-data-tabs-request-params";
import { usePatchedWidgetComponentProps, useWidgetComponentProps, useWidgetCurrentEndDate, useWidgetCurrentGranularity, useWidgetCurrentStartDate, useWidgetMode } from "./use-current-widget";
import { DatapointFormatEnum, DatapointIdStandardEnum, DatapointsEntityType, DatapointTimeseriesEnum, useGetDatapointsMetadata } from "api/ux/src";
import { inspectData } from "common/constants";
import { ModalName } from "components/modal/types";
import { InspectDataTab } from "components/modal/widget-inspect-data/types";
import { getColumnDefs } from "components/modal/widget-inspect-data/utils";
import { useGetEntityInfo } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { useWidgetFilterValues } from "components/workspaces/hooks/use-get-filter-values";
import { Permissions, usePermission } from "feature-flag";
import { useGetDatapointsAllowedDimensions } from "hooks/use-get-datapoints-allowed-dimensions";
import { excludedDatapointsIdList } from "hooks/use-get-datapoints-metadata-and-allowed-dimensions";
import { getCreateDatapointsRequest, getCreateWidgetRequest, getTimeSeries, useCreateDatapointsRequest, useCreateWidgetRequest, useGetCreateDatapointsRequestDependencies, useGetDatapointsWidgetParams, useGetEntityComparates } from "hooks/widget-data/use-datapoints";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { setCurrentWidgetFilterValues } from "store/current-dashboard";
import { WidgetMode } from "store/current-dashboard/types";
import { useGetGridGroups } from "store/grids";
import { createInspectDataWidget } from "store/inspectdata";
import { openModal } from "store/modal";
import { getCurrentLocalTime } from "common/date-utils";
export const excludedDatapointsForInspectData = excludedDatapointsIdList?.concat([
    DatapointIdStandardEnum.FundLogoUrl,
    DatapointIdStandardEnum.ManagerLogoUrl,
    DatapointIdStandardEnum.AssetLogoUrl
]);
export const createClickthroughDatapointsDictionary = (datapointsMetadata) => datapointsMetadata
    .reduce((acc, curr) => {
    if (curr.metadata?.baseDataPointId && curr.id) {
        acc[curr.id] = true;
    }
    return acc;
}, {});
export const useCreateDatapointsRequestForInspectData = (requestParams, isComparisonWidget, datapointsMetadata) => {
    const { entityType, timePeriod, widgetCurrentStatusPeriodOfAnalysis } = useGetCreateDatapointsRequestDependencies(requestParams);
    const [allowedDimensions, requestStatus] = useGetDatapointsAllowedDimensions();
    const haveClickthrough = usePermission([Permissions.CLICKTHROUGH]);
    const clickthroughDatapoints = haveClickthrough ? createClickthroughDatapointsDictionary(datapointsMetadata) : null;
    const isRequestCreationFinished = requestStatus?.isFinished ?? false;
    return {
        inspectDataTabs: requestStatus?.isFinished
            ? getInspectDataTabsRequestParams({
                clickthroughDatapoints,
                datapointsAllowedDimensions: allowedDimensions,
                entityType,
                forceUseOriginalRequest: false,
                isComparisonWidget,
                periodOfAnalysis: widgetCurrentStatusPeriodOfAnalysis,
                timePeriod,
                widgetParams: requestParams
            })
            : { ActualsAndProjections: [], Asset: [], Cashflows: [], CommitmentPacingOutputs: [], FullHistory: [], Fund: [], Portfolio: [] },
        isRequestCreationFinished
    };
};
export const useGetDatapointsWidgetParamsForDataMenuItem = (props) => {
    const widgetProps = usePatchedWidgetComponentProps(props.sectionId, props.widgetId);
    let params = useGetDatapointsWidgetParams(props, widgetProps);
    const grouped = useGetGridGroups(getDatapointGridStateId(props.sectionId, props.widgetId), widgetProps?.columnsTemplate?.columns);
    const paramsChildren = useGetDatapointsWidgetParams({ ...props, widgetId: widgetProps.childrenId }, usePatchedWidgetComponentProps(props.sectionId, widgetProps.childrenId)), { entityType } = useGetCreateDatapointsRequestDependencies(params);
    if (widgetProps.childrenId) {
        params = paramsChildren;
    }
    filterHideParamsForInspectData(widgetProps?.columnsTemplate?.columns, params, grouped);
    if (params[0]?.measures) {
        params[0].measures = uniqWith(params[0].measures.map((d) => ({ ...d })), (a, b) => a.standardDatapoint === b.standardDatapoint && a.name === b.name &&
            a.customDatapoint === b.customDatapoint && a.format === b.format).filter((m) => m.standardDatapoint !== DatapointIdStandardEnum.AssetInvestorName);
        if (entityType === DatapointsEntityType.Manager) {
            params[0].measures = params[0].measures.filter((n) => n.standardDatapoint !== DatapointIdStandardEnum.FundManagerName);
        }
    }
    return { entityType, params };
};
const useAddPostDatapointsRequest = (params, colDefs, sheetName, fullHistory) => {
    const request = useCreateDatapointsRequest(params);
    if (colDefs?.length && request?.datapointsRequest?.requests?.length) {
        const requestSinceInception = cloneDeep(request);
        for (const aRequest of requestSinceInception.datapointsRequest?.requests || []) {
            aRequest?.measures?.forEach((m) => {
                // Inspect data should export raw value and not %age of allocation
                if (m.format === DatapointFormatEnum.Percent) {
                    delete m.format;
                }
            });
            if (fullHistory) {
                const timeSeries = aRequest?.measures?.map((m) => m.timeseries)?.[0] || DatapointTimeseriesEnum.EndOfQuarter;
                aRequest?.measures?.forEach((m) => {
                    delete m.periods;
                    m.timeseries = timeSeries;
                });
            }
        }
        return { colDefs, request: requestSinceInception, sheetName };
    }
    return {};
};
const useAddPostDatapointsComparisonRequest = (params, mode) => useCreateWidgetRequest(params, mode);
export const getAddPostDatapointsRequest = (params, colDefs, sheetName, fullHistory, config) => {
    const request = getCreateDatapointsRequest(params, config);
    if (colDefs?.length && request?.datapointsRequest?.requests?.length) {
        const requestSinceInception = cloneDeep(request);
        for (const aRequest of requestSinceInception.datapointsRequest?.requests || []) {
            aRequest?.measures?.forEach((m) => {
                // Inspect data should export raw value and not %age of allocation
                if (m.format === DatapointFormatEnum.Percent) {
                    delete m.format;
                }
            });
            if (fullHistory) {
                const timeSeries = aRequest?.measures?.map((m) => m.timeseries)?.[0] ?? DatapointTimeseriesEnum.EndOfQuarter;
                aRequest?.measures?.forEach((m) => {
                    delete m.periods;
                    m.timeseries = timeSeries;
                });
            }
        }
        return { colDefs, request: requestSinceInception, sheetName };
    }
    return {};
};
export const getPrepareRequestForExcelExport = (request, colDefs, comparates, config, isComparisonWidget, isPacingTimeserie) => {
    const allDatapointsRequests = {
        ActualsAndProjections: isPacingTimeserie
            ? getAddPostDatapointsRequest(request?.ActualsAndProjections, colDefs?.ActualsAndProjections, "Actuals and Projections", false, config)
            : undefined,
        Asset: getAddPostDatapointsRequest(request?.Asset, colDefs?.Asset, "Inspect Assets", true, config),
        Cashflows: getAddPostDatapointsRequest(request?.Cashflows, colDefs?.Cashflows, "Inspect Cashflows", true, config),
        CommitmentPacingOutputs: isPacingTimeserie
            ? getAddPostDatapointsRequest(request?.CommitmentPacingOutputs, colDefs?.CommitmentPacingOutputs, "Commitment Pacing Output", false, config)
            : undefined,
        Fund: getAddPostDatapointsRequest(request?.Fund, colDefs?.Fund, "Inspect Funds", true, config),
        Portfolio: getAddPostDatapointsRequest(request?.Portfolio, colDefs?.Portfolio, "", false, config),
        SingleTab: getAddPostDatapointsRequest(request?.FullHistory, colDefs?.FullHistory, "Inspect Data", false, config)
    }, comparisonRequest = getCreateWidgetRequest(isEmpty(request.Fund) ? request.Asset : request.Fund, comparates, config, isComparisonWidget ? WidgetMode.Comparison : WidgetMode.Normal);
    return {
        allDatapointsRequests,
        comparisonRequest
    };
};
export const getInspectDataComponentPropsForExcelExport = (isComparisonWidget, comparisonRequest, allDatapointsRequests, entityName, showTotals, subEntityName, isPacingTimeserie) => {
    if (isComparisonWidget) {
        return getDatapointsExcelComparisonRequestQueryConfig(comparisonRequest, [allDatapointsRequests.Portfolio, isEmpty(allDatapointsRequests.Fund) ? allDatapointsRequests.Asset : allDatapointsRequests.Fund], entityName);
    }
    let datapointsRequests = [allDatapointsRequests.SingleTab];
    if (allDatapointsRequests?.Asset?.request?.datapointsRequest?.requests?.length || allDatapointsRequests?.Fund?.request?.datapointsRequest?.requests?.length ||
        allDatapointsRequests?.Cashflows?.request?.datapointsRequest?.requests?.length) {
        datapointsRequests = [allDatapointsRequests.Asset, allDatapointsRequests.Fund, allDatapointsRequests.Cashflows];
    }
    if (isPacingTimeserie) {
        datapointsRequests.push(allDatapointsRequests.ActualsAndProjections);
        datapointsRequests.push(allDatapointsRequests.CommitmentPacingOutputs);
    }
    const postRequests = datapointsRequests.filter((r) => r?.request).map((r) => ({
        colDefs: r.colDefs,
        request: r.request,
        sheetName: r.sheetName,
        showTotals,
        userSelectedGroups: []
    }));
    return getDatapointsExcelRequestQueryConfig(postRequests, entityName, subEntityName);
};
export const useGetInspectDataComponentPropsForExcelExport = (props, request, colDefs, isComparisonWidget) => {
    const allDatapointsRequests = {
        Asset: useAddPostDatapointsRequest(request?.Asset, colDefs?.Asset, "Inspect Assets", true),
        Cashflows: useAddPostDatapointsRequest(request?.Cashflows, colDefs?.Cashflows, "Inspect Cashflows", true),
        Fund: useAddPostDatapointsRequest(request?.Fund, colDefs?.Fund, "Inspect Funds", true),
        Portfolio: useAddPostDatapointsRequest(request?.Portfolio, colDefs?.Portfolio, "", false),
        SingleTab: useAddPostDatapointsRequest(request?.FullHistory, colDefs?.FullHistory, "Inspect Data", false)
    }, comparisonRequest = useAddPostDatapointsComparisonRequest(isEmpty(request.Fund) ? request.Asset : request.Fund, isComparisonWidget ? WidgetMode.Comparison : WidgetMode.Normal), entityInfo = useGetEntityInfo(), subEntityName = useGetSubEntityName(), { userSettings } = useWidgetComponentProps(props.sectionId, props.widgetId) || {};
    if (isComparisonWidget) {
        return getDatapointsExcelComparisonRequestQueryConfig(comparisonRequest, [allDatapointsRequests.Portfolio, isEmpty(allDatapointsRequests.Fund) ? allDatapointsRequests.Asset : allDatapointsRequests.Fund], entityInfo.name);
    }
    let datapointsRequests = [allDatapointsRequests.SingleTab];
    if (allDatapointsRequests?.Asset?.request?.datapointsRequest?.requests?.length || allDatapointsRequests?.Fund?.request?.datapointsRequest?.requests?.length ||
        allDatapointsRequests?.Cashflows?.request?.datapointsRequest?.requests?.length) {
        datapointsRequests = [allDatapointsRequests.Asset, allDatapointsRequests.Fund, allDatapointsRequests.Cashflows];
    }
    const postRequests = datapointsRequests.filter((r) => r?.request).map((r) => ({
        colDefs: r.colDefs,
        request: r.request,
        sheetName: r.sheetName,
        showTotals: userSettings?.displayOptions?.showTotals || false,
        userSelectedGroups: []
    }));
    return getDatapointsExcelRequestQueryConfig(postRequests, entityInfo?.name, subEntityName);
};
const hasOverrides = (datapointsMetadata) => {
    const overrides = datapointsMetadata.metadata?.inspectDataOverride;
    if (!overrides) {
        return false;
    }
    const overridesArray = overrides;
    if (overridesArray.length === 0) {
        return false;
    }
    return true;
};
const findDatapoint = (measure) => (datapoint) => datapoint.id === measure.standardDatapoint;
const generateOverrideMeasure = (paramMeasures, paramDimensions, allowedDimensions, defaultTimeSeries, currentMeasure) => (override) => {
    const overridenMeasure = paramMeasures.some((m) => m.standardDatapoint === override);
    const overridenDimension = paramDimensions.some((d) => d.standardDatapoint === override);
    const shouldOverrideDimension = allowedDimensions.some((d) => d.datapoint?.standard === override);
    if (overridenMeasure || overridenDimension || !shouldOverrideDimension) {
        return false;
    }
    return {
        format: currentMeasure.format,
        name: override.replace(".", ""),
        periods: currentMeasure.periods,
        projected: currentMeasure.projected,
        standardDatapoint: override,
        timeSeries: defaultTimeSeries ?? currentMeasure.timeSeries
    };
};
const compareDatapoints = (a, b) => a.standardDatapoint === b.standardDatapoint &&
    a.customDatapoint === b.customDatapoint &&
    a.format === b.format &&
    a.name === b.name &&
    a?.isRelativeColumn === b?.isRelativeColumn;
const applyParamsOverrides = (applyInspectDataOverride, params, datapointsMetadata, allowedDimensions, timePeriod) => {
    const [firstParam, ...otherParams] = params;
    if (!applyInspectDataOverride || !firstParam?.measures) {
        return params;
    }
    const metaDataOverrides = datapointsMetadata.filter(hasOverrides);
    const defaultTimeSeries = getTimeSeries(timePeriod);
    return [
        {
            ...firstParam,
            measures: [
                ...firstParam.measures,
                ...firstParam.measures.reduce((newMeasures, measure) => {
                    const overrides = metaDataOverrides.find(findDatapoint(measure));
                    if (!overrides) {
                        return newMeasures;
                    }
                    const overridesArray = overrides.metadata?.inspectDataOverride;
                    const overridenMeasures = overridesArray
                        .map(generateOverrideMeasure(firstParam.measures, firstParam.dimensions || [], allowedDimensions, defaultTimeSeries, measure))
                        .filter((a) => a);
                    return uniqWith([
                        ...newMeasures,
                        ...overridenMeasures
                    ], (a, b) => a.name === b.name);
                }, [])
            ]
        },
        ...otherParams
    ];
};
export const getUpdatedParamsAndColDefs = ({ applyInspectDataOverride, params, datapointsMetadata, allowedDimensions, timePeriod, entityType, timePeriod2, isComparisonWidget, widgetCurrentStatusPeriodOfAnalysis, haveClickthrough, startDate, endDate, forceUseOriginalRequest }) => {
    const newParams = forceUseOriginalRequest
        ? params
        : applyParamsOverrides(applyInspectDataOverride, params, datapointsMetadata, allowedDimensions, timePeriod);
    const clickthroughDatapoints = haveClickthrough ? createClickthroughDatapointsDictionary(datapointsMetadata) : null;
    const inspectDataTabs = getInspectDataTabsRequestParams({
        clickthroughDatapoints,
        datapointsAllowedDimensions: allowedDimensions,
        endDate,
        entityType,
        forceUseOriginalRequest,
        isComparisonWidget,
        periodOfAnalysis: widgetCurrentStatusPeriodOfAnalysis,
        startDate,
        timePeriod: timePeriod2,
        widgetParams: newParams
    });
    const columnDefs = Object.values(InspectDataTab).reduce((colDefs, level) => {
        if (inspectDataTabs?.[level] === undefined) {
            return colDefs;
        }
        if (inspectDataTabs[level]?.length === 0) {
            return colDefs;
        }
        const rawDatapoints = uniqWith([
            ...inspectDataTabs[level][0]?.measures ?? [],
            ...inspectDataTabs[level][0]?.dimensions ?? []
        ], compareDatapoints);
        const datapoints = rawDatapoints.filter((d) => !d.hideInInspectData);
        if (datapoints?.length === 0) {
            return colDefs;
        }
        return {
            ...colDefs,
            [level]: getColumnDefs(datapoints, datapointsMetadata)
        };
    }, { ActualsAndProjections: [],
        Asset: [],
        Cashflows: [],
        CommitmentPacingOutputs: [],
        FullHistory: [],
        Fund: [],
        Portfolio: [] });
    return { columnDefs, inspectDataTabs };
};
export const isSameWidget = (metadata) => metadata.widgetId === inspectData.widgetId;
export const isParamsEmpty = (params) => !params || params.length === 0;
export const shouldReturnNull = (metadata, params, widgetWithoutData) => isSameWidget(metadata) ||
    isParamsEmpty(params) ||
    widgetWithoutData;
export const getWidgetSectionAndIdFromProps = (props) => ({
    sectionId: props.sectionId ?? "",
    widgetId: props.widgetId ?? ""
});
export const shouldDisableInspectdata = (widgetCompProps) => widgetCompProps?.disableInspectData || false;
export const useInspectDataConfig = (metadata) => {
    const dispatch = useDispatch(), [datapointsMetadata] = useGetDatapointsMetadata(), props = { index: 0, sectionId: metadata.sectionId ?? "", widgetId: metadata.widgetId ?? "" };
    const { entityType, params } = useGetDatapointsWidgetParamsForDataMenuItem(props);
    const [allowedDimensions] = useGetDatapointsAllowedDimensions();
    const applyInspectDataOverride = entityType !== DatapointsEntityType.Market && entityType !== DatapointsEntityType.FundSeries;
    const isComparisonWidget = useWidgetMode(metadata.widgetId) === WidgetMode.Comparison;
    const { sectionId, widgetId } = getWidgetSectionAndIdFromProps(props);
    const widgetFilters = useWidgetFilterValues(sectionId, widgetId) ?? {};
    const timePeriod = useWidgetCurrentGranularity(sectionId, widgetId);
    const haveClickthrough = usePermission([Permissions.CLICKTHROUGH]);
    const requestParameterConfig = useGetCreateDatapointsRequestDependencies(params);
    const entityInfo = useGetEntityInfo();
    const subEntityName = useGetSubEntityName();
    const { userSettings, filterCashflows } = useWidgetComponentProps(sectionId, widgetId) || {};
    const comparates = useGetEntityComparates(isComparisonWidget);
    const startDate = useWidgetCurrentStartDate(metadata.sectionId, metadata.widgetId);
    const endDate = useWidgetCurrentEndDate(metadata.sectionId, metadata.widgetId);
    const { entityType: entityType2, tenor: tenor2, timePeriod: timePeriod2, widgetCurrentStatusPeriodOfAnalysis: widgetCurrentStatusPeriodOfAnalysis2 } = useGetCreateDatapointsRequestDependencies(params);
    const widgetCompProps = usePatchedWidgetComponentProps(sectionId, widgetId);
    const widgetWithoutData = useSelector((state) => state.widgetsWithoutData?.[widgetId || ""], isEqual);
    if (shouldReturnNull(metadata, params, widgetWithoutData)) {
        return null;
    }
    const { inspectDataTabs, columnDefs } = getUpdatedParamsAndColDefs({
        allowedDimensions,
        applyInspectDataOverride,
        datapointsMetadata,
        endDate,
        entityType: entityType2,
        forceUseOriginalRequest: widgetCompProps?.forceUseOriginalRequestInExportExcel,
        haveClickthrough,
        isComparisonWidget,
        params,
        startDate,
        timePeriod,
        timePeriod2,
        widgetCurrentStatusPeriodOfAnalysis: widgetCurrentStatusPeriodOfAnalysis2
    });
    const actions = [];
    if (shouldDisableInspectdata(widgetCompProps) === false) {
        actions.push({
            action: () => {
                batch(() => {
                    dispatch(createInspectDataWidget({ columnDefs,
                        filterCashflows,
                        isComparisonWidget: isComparisonWidget ?? false,
                        metadata,
                        params: inspectDataTabs,
                        tenor: tenor2 }));
                    dispatch(openModal({
                        content: { id: `${metadata.sectionId}-${metadata.widgetId}-inspect-data-modal`, metadata, modalName: ModalName.InspectData },
                        title: language.en.widget.inspectData,
                        type: "action"
                    }));
                    dispatch(setCurrentWidgetFilterValues({ filters: Object.entries(widgetFilters).map(([k, v]) => ({ filter: k, values: v })), widget: inspectData.widgetId }));
                });
                appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.OpenInspectData, properties: { LocalTimestamp: getCurrentLocalTime() } });
            },
            hasEmptyIcon: true,
            key: `${metadata.sectionId}-${metadata.widgetId}-inspect-data-menu-item`,
            label: `${language.en.widget.inspectData}`
        });
    }
    actions.push({
        action: () => {
            const { allDatapointsRequests, comparisonRequest } = getPrepareRequestForExcelExport(inspectDataTabs, columnDefs, comparates, requestParameterConfig, isComparisonWidget, isPacingTimeseries(params));
            const excelExportDatapointsRequestQueryConfig = getInspectDataComponentPropsForExcelExport(isComparisonWidget, comparisonRequest, allDatapointsRequests, entityInfo?.name ?? "", userSettings?.displayOptions?.showTotals || false, subEntityName ?? "", isPacingTimeseries(params));
            dispatch(requestAsync(excelExportDatapointsRequestQueryConfig));
            appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.WidgetExportToExcel, properties: { LocalTimestamp: getCurrentLocalTime() } });
        },
        key: `${metadata.sectionId}-${metadata.widgetId}-export-inspect-data-menu-item`,
        label: `${language.en.widget.inspectDataExport}`,
        leftIcon: "export"
    });
    return actions;
};
